import { useRef, useMemo, useState, useEffect } from 'react';
import SquarePaymentForm, {
  type SquarePaymentFormHandle,
} from './SquarePaymentForm';
import type { CheckoutConfig } from '../lib/checkout';
import type { ValidateCouponResult } from '../lib/coupons';
import type { ShippingEvaluateResult } from '../lib/coupons';

interface CartItem {
  sku: string;
  title: string;
  price: number;
  brand?: string;
  size?: string;
  photoUrl?: string;
  siteName: string;
  siteId: number;
}

type ShippingMethod = 'pickup' | 'canadapost' | 'ups';

interface PaymentStepProps {
  items: CartItem[];
  shippingMethod: ShippingMethod;
  customerContact: { name: string; email: string; phone: string };
  couponApplied: ValidateCouponResult | null;
  shippingEval: ShippingEvaluateResult | null;
  checkoutConfig: CheckoutConfig;
  checkoutState: 'idle' | 'processing' | 'success' | 'error';
  checkoutError: string | null;
  onCheckout: (sourceId: string, postalCode: string) => void;
  onBack: () => void;
}

const SHIPPING_PRICES: Record<ShippingMethod, number> = {
  pickup: 0,
  canadapost: 0,
  ups: 0,
};

export default function PaymentStep({
  items,
  shippingMethod,
  customerContact,
  couponApplied,
  shippingEval,
  checkoutConfig,
  checkoutState,
  checkoutError,
  onCheckout,
  onBack,
}: PaymentStepProps) {
  const squareFormRef = useRef<SquarePaymentFormHandle>(null);
  const [paymentReady, setPaymentReady] = useState(false);
  // Disable Pay immediately on click (before async tokenize) to prevent double /pay
  const [payClicked, setPayClicked] = useState(false);

  useEffect(() => {
    if (checkoutState === 'error' || checkoutState === 'idle') {
      setPayClicked(false);
    }
  }, [checkoutState]);

  const merchandiseDiscount =
    couponApplied?.ok && couponApplied.benefit_type === 'item'
      ? (couponApplied.discount_cents || 0) / 100
      : 0;

  const subtotal = items.reduce((sum, item) => sum + item.price, 0);
  const discountedSubtotal = Math.max(0, subtotal - merchandiseDiscount);

  const baseShippingCost = SHIPPING_PRICES[shippingMethod] ?? 0;
  const autoShipDiscount = (shippingEval?.shipping_discount_cents || 0) / 100;
  const couponShipDiscount =
    couponApplied?.ok && couponApplied.benefit_type === 'shipping'
      ? (couponApplied.shipping_discount_cents || 0) / 100
      : 0;
  const shippingDiscount = Math.max(autoShipDiscount, couponShipDiscount);
  const shippingCost = Math.max(0, baseShippingCost - shippingDiscount);
  const estimatedTotal = discountedSubtotal + shippingCost;

  const itemSummary = useMemo(() => {
    if (items.length <= 3) {
      return items.map((i) => i.title || i.sku).join(', ');
    }
    return `${items[0].title || items[0].sku} and ${items.length - 1} more`;
  }, [items]);

  const handlePay = async () => {
    if (payClicked || checkoutState === 'processing') return;
    setPayClicked(true);
    try {
      const sourceId = await squareFormRef.current?.tokenize({
        amount: estimatedTotal.toFixed(2),
        currencyCode: 'CAD',
      });
      if (!sourceId) throw new Error('Payment form is not ready');
      const postalCode = squareFormRef.current?.getPostalCode() || '';
      onCheckout(sourceId, postalCode);
    } catch (err) {
      // tokenize failure is handled by Square form UI — allow retry
      setPayClicked(false);
    }
  };

  return (
    <>
      <div className="card">
        <h2>Review &amp; Pay</h2>
        <div className="payment-review">
          <div className="payment-review-row">
            <span className="payment-review-label">Items</span>
            <span className="payment-review-value">{itemSummary}</span>
          </div>
          <div className="payment-review-row">
            <span className="payment-review-label">Fulfillment</span>
            <span className="payment-review-value">
              {shippingMethod === 'pickup' ? 'In-Store Pickup' : 'Shipping'}
            </span>
          </div>
          <div className="payment-review-row">
            <span className="payment-review-label">Contact</span>
            <span className="payment-review-value">
              {customerContact.name} ({customerContact.email})
            </span>
          </div>
        </div>
      </div>

      <div className="card">
        <h2>Order Total</h2>
        <div className="cart-summary">
          <span>Subtotal</span>
          <span>${subtotal.toFixed(2)}</span>
        </div>
        {merchandiseDiscount > 0 && (
          <div className="cart-summary discount">
            <span>Coupon {couponApplied?.code}</span>
            <span>&minus;${merchandiseDiscount.toFixed(2)}</span>
          </div>
        )}
        <div className="cart-summary">
          <span>Tax</span>
          <span style={{ color: '#888', fontSize: '0.9em' }}>Calculated by server</span>
        </div>
        <div className="cart-summary">
          <span>{shippingMethod === 'pickup' ? 'In-Store Pickup' : 'Shipping'}</span>
          <span>{shippingCost === 0 ? 'Free' : `$${shippingCost.toFixed(2)}`}</span>
        </div>
        <div className="cart-summary total">
          <span>Estimated Total</span>
          <span className="total-price">${estimatedTotal.toFixed(2)}+ CAD</span>
        </div>
        <p className="cart-summary-note">
          Applicable taxes (GST, PST, HST) will be added based on your shipping destination.
        </p>
        {checkoutConfig.environment === 'sandbox' && (
          <p className="cart-summary-note">
            Sandbox checkout \u2014 no real charges.
          </p>
        )}
      </div>

      {checkoutConfig.enabled && (
        <div className="card">
          <h2>Payment</h2>
          <SquarePaymentForm
            ref={squareFormRef}
            config={checkoutConfig}
            disabled={checkoutState === 'processing' || items.length === 0}
            onReadyChange={setPaymentReady}
          />
        </div>
      )}

      {checkoutError && (
        <div className="status-banner error">{checkoutError}</div>
      )}

      <div className="step-actions">
        <button
          className="btn btn-primary"
          onClick={handlePay}
          disabled={
            payClicked ||
            checkoutState === 'processing' ||
            !checkoutConfig.enabled ||
            !paymentReady ||
            items.length === 0
          }
        >
          {payClicked || checkoutState === 'processing'
            ? 'Processing\u2026'
            : `Pay $${estimatedTotal.toFixed(2)}+ CAD`}
        </button>
        <button
          className="btn btn-secondary"
          onClick={onBack}
          disabled={payClicked || checkoutState === 'processing'}
        >
          Back to Shipping
        </button>
      </div>
    </>
  );
}
